// Tiny form validator — replaces yup for the site's lead and comment forms.
//
// It deliberately mirrors how Formik ran yup, so every form shows exactly the same message
// in exactly the same situation as before:
//   1. Formik turned every '' into `undefined` before validating ("absent").
//   2. An absent field fails with its `required` message (yup's `.required()`), otherwise it
//      is valid — rules never see an absent value (yup's `skipAbsent`).
//   3. `trim` is applied first (yup's `.trim()` transform), then the rules run in the order
//      they were declared in the yup chain, and the FIRST failing rule's message wins
//      (Formik keeps the first yup error per field).

export type FormValues = object;

/** A rule receives a present (non-absent) value and every form value; returns an error message or undefined. */
export type Rule<V extends FormValues> = (value: any, values: V) => string | undefined;

export interface FieldSpec<V extends FormValues> {
    /** Message shown when the field is absent ('' / undefined / null). Omit for optional fields. */
    required?: string;
    /** Trim string values before the rules run. */
    trim?: boolean;
    /** Checks in declaration order; the first failure wins. */
    rules?: Rule<V>[];
}

export type FormSchema<V extends FormValues> = {[K in keyof V]?: FieldSpec<V>};

export type FormErrors<V extends FormValues> = {[K in keyof V]?: string};

const isAbsent = (value: unknown): boolean => value === undefined || value === null || value === '';

/** Formik's `prepareDataForValidation`: empty strings become undefined. */
const prepare = <V extends FormValues>(values: V): V =>
    Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value === '' ? undefined : value])) as V;

export const validateForm = <V extends FormValues>(schema: FormSchema<V>, values: V): FormErrors<V> => {
    const prepared = prepare(values);
    const errors: FormErrors<V> = {};

    (Object.keys(schema) as Array<keyof V>).forEach((field) => {
        const spec = schema[field];
        if (!spec) return;

        let value: unknown = prepared[field];
        if (isAbsent(value)) {
            if (spec.required) errors[field] = spec.required;
            return;
        }

        if (spec.trim && typeof value === 'string') value = value.trim();

        for (const rule of spec.rules ?? []) {
            const message = rule(value, prepared);
            if (message) {
                errors[field] = message;
                return;
            }
        }
    });

    return errors;
};

// ─── Rules ────────────────────────────────────────────────────────────────────

/** yup `.matches()` — note it tests with `search`, so the regex's `lastIndex` never matters. */
export const matches =
    <V extends FormValues>(pattern: RegExp, message: string): Rule<V> =>
    (value) =>
        String(value).search(pattern) === -1 ? message : undefined;

/** yup string `.max()`. */
export const maxLength =
    <V extends FormValues>(max: number, message: string): Rule<V> =>
    (value) =>
        String(value).length > max ? message : undefined;

/** yup string `.required()`'s length check — fails a value that trimmed down to ''. */
export const notEmpty =
    <V extends FormValues>(message: string): Rule<V> =>
    (value) =>
        String(value).length === 0 ? message : undefined;

/** yup `.oneOf([true])` for a boolean field such as the captcha flag. */
export const isTrue =
    <V extends FormValues>(message: string): Rule<V> =>
    (value) =>
        value === true ? undefined : message;

/** yup `.test()` — `check` returns true when the value is valid. */
export const check =
    <V extends FormValues>(isValid: (value: any, values: V) => boolean, message: string): Rule<V> =>
    (value, values) =>
        isValid(value, values) ? undefined : message;
